import React, { useCallback, useState } from "react";
import * as yup from "yup";
import { InferType } from "yup";
import {
  DISMISS,
  Form,
  Formik,
  Input,
  InputProps,
  Select,
  ToggleSwitch,
  useNotification
} from "collections";
import i18n, { useTranslation } from "utilities/i18n";
import { Link, RouteComponentProps, useHistory } from "polyrouter";
import {
  NcIntegrationActionConfig,
  Rate,
  ServiceNowHookPageFragment,
  TestNewServiceNowWebhookMutationVariables,
  useCreateServiceNowWebhookForTenantMutation,
  useNcIntegrationsQuery,
  useTestNewServiceNowWebhookMutation,
  useUpdateServiceNowActionConfigurationMutation
} from "generated/graphql";
import { useServiceNowNotificationsConfiguration } from "../TenantServiceNow";
import { NOTIFICATION_ACTION_TYPES } from "Constants";
import { getNotificationToDisplay } from "../helper";
import { SubmitButton } from "elements";
import { getFrequencyOptions } from "../../Helpers";
import { TestConnectionButton } from "elements/TestConnectionButton";

export const schema = yup.object({
  name: yup
    .string()
    .default("")
    .test("Name required", i18n.t("NAME_IS_REQUIRED"), function(name) {
      return !(name.length === 0);
    })
    .test(
      "Duplicate Webhook error",
      i18n.t("ERROR_DUPLICATE_INTEGRATION_HOOK"),
      function(name) {
        const { hooks } = this.parent;

        if (name.length) {
          const foundHook = !!hooks.find(
            (x: { name: string }) => x.name.toUpperCase() === name.toUpperCase()
          );
          return !foundHook;
        } else {
          return true;
        }
      }
    )
    .label(i18n.t("NAME"))
    .required(),
  url: yup
    .string()
    .url()
    .default("")
    .test("Invalid URL path", i18n.t("URL_IS_REQUIRED"), function(url) {
      return !(url.length === 0);
    })
    .test("Invalid URL path", i18n.t("ERROR_INVALID_URL_FORMAT"), function(
      url
    ) {
      if (url.length) {
        return url.endsWith("/api/global/em/jsonv2");
      }
    })
    .label(i18n.t("URL"))
    .required(),
  username: yup
    .string()
    .label(i18n.t("USERNAME"))
    .required(),
  password: yup
    .string()
    .label(i18n.t("PASSWORD"))
    .required(),
  actionTypes: yup.array().of(
    yup.object({
      enabled: yup.bool(),
      actionType: yup.object({ id: yup.string(), type: yup.string() }),
      rate: yup.string(),
      allowedRate: yup.array().of(yup.string())
    })
  ),
  hooks: yup
    .array()
    .of(yup.object())
    .default([])
});

export const InitialValuesInit = (
  actionConfigs: any,
  hooks?: ServiceNowHookPageFragment[]
) => {
  let configs = actionConfigs as NcIntegrationActionConfig[];

  if (configs == null) {
    configs = [];
  }
  return {
    name: "",
    url: "",
    username: "",
    password: "",
    actionTypes: configs.map((at: any) => ({
      enabled: false,
      actionType: at!.type,
      rate: Rate.Interval_15M,
      allowedRate: [Rate.Interval_15M]
    })),
    hooks: hooks || []
  };
};

export type CreateType = InferType<typeof schema>;

export const FormInput: React.FC<InputProps> = ({
  name,
  label,
  isValid,
  ...rest
}) => {
  return (
    <div
      className={`form-group${
        isValid !== undefined && !isValid ? " error-message-spacing" : ""
      }`}
    >
      <label className="col-3 text-right" htmlFor={name + "-input"}>
        {label}:
      </label>
      <div className="col-5">
        <Input
          {...rest}
          name={name}
          data-testid={name}
          type={
            name.toLocaleLowerCase() === "password"
              ? name.toLocaleLowerCase()
              : ""
          }
        />
      </div>
    </div>
  );
};

interface SubmitFormValues {
  name: string;
  url: string;
  username: string;
  password: string;
  actionTypes: any[];
}

/**
 * Provide a shared submit action handler for forms to test a webhook.
 * You can call this on its own, or provide it to a form's onSubmit to also track
 * the submission process.
 */
export const useTestFormBehavior = () => {
  const [addNotification] = useNotification();
  const [, test] = useTestNewServiceNowWebhookMutation();
  return useCallback(
    async (params: TestNewServiceNowWebhookMutationVariables) => {
      const { data, error } = await test(params, {
        requestPolicy: "network-only"
      });
      if (error) return; //Notification of an invocation error is handled by the exchange

      const statusCode = data?.ncTestServiceNowWebhookWithParams.statusCode;
      const statusMessage =
        data?.ncTestServiceNowWebhookWithParams.statusMessage;
      const messageBody = data?.ncTestServiceNowWebhookWithParams.body;

      const success =
        statusCode !== undefined &&
        statusCode !== null &&
        statusCode > 199 &&
        statusCode < 300;

      const type = success ? "success" : "error";
      addNotification({
        type,
        message: getNotificationToDisplay(statusMessage, messageBody),
        dismiss: success ? DISMISS.SLOW : DISMISS.USER_ONLY
      });
    },
    // omit t and addNotification from deps, otherwise causes useEffect to call infinitely with no usefulness
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [test]
  );
};

export const CreateServiceNowHook: React.FC<RouteComponentProps<{
  id: string;
}>> = ({
  match: {
    params: { id }
  }
}) => {
  const [showTestSpinner, setShowTestSpinnerValue] = useState(false);
  const { t } = useTranslation();
  const h = useHistory();
  const [, create] = useCreateServiceNowWebhookForTenantMutation();
  const [, update] = useUpdateServiceNowActionConfigurationMutation();
  const { data } = useServiceNowNotificationsConfiguration(id);
  const hooks = data?.tenant.serviceNowWebhooks ?? [];
  const test = useTestFormBehavior();
  const { data: integrationData, fetching } = useNcIntegrationsQuery({
    variables: {
      input: {
        name: {
          eq: "service_now"
        }
      }
    }
  });

  const actionConfigurations =
    integrationData?.notificationCenter?.integrations[0].actionConfigurations;

  if (fetching) {
    return null;
  }

  const initialValues = InitialValuesInit(actionConfigurations ?? [], hooks);

  const onSubmit = async ({
    name,
    username,
    password,
    url,
    actionTypes
  }: SubmitFormValues) => {
    const created = await create({
      tenantId: id,
      input: {
        enabled: true,
        name,
        url,
        username,
        password
      }
    });
    const actions =
      created.data?.ncCreateServiceNowWebhook?.actionConfigurations.map(
        async c => {
          const match = actionTypes.find(
            at => at.actionType.id === c.actionType.id
          );
          if (!match) return Promise.resolve();
          return update({
            input: {
              actionId: c.id,
              enabled: match.enabled,
              rate: match.rate as Rate
            }
          });
        }
      ) ?? [];
    await Promise.all(actions);
    h.goBack();
  };

  return (
    <Formik<CreateType>
      name="CreateServiceNowHook"
      validateOnMount
      initialValues={initialValues}
      validationSchema={schema}
      onSubmit={onSubmit}
    >
      {({ isSubmitting, isValid, field, values }) => (
        <Form className="form grid">
          <div
            className="mt-15 ph-2"
            data-testid="create-servicenow-hook-header"
          >
            <label>{t("NOTIFICATION_SERVICE_NEW")}</label>
          </div>
          {/* Error present is only relative on this property since it is on top and cuts off the input below it */}
          <FormInput
            {...field.name}
            isValid={isValid}
            label={field.name.label}
          />
          <FormInput
            {...field.url}
            isValid={isValid}
            label={field.url.label}
            placeholder={t("SERVICE_NOW_URL_EXAMPLE")}
          />
          <FormInput
            {...field.username}
            isValid={isValid}
            label={field.username.label}
            placeholder={t("SERVICE_NOW_USERNAME")}
          />
          <FormInput
            {...field.password}
            isValid={isValid}
            label={field.password.label}
            placeholder={t("SERVICE_NOW_PASSWORD")}
          />
          <div className="mt-15 ph-2">
            <label>{t("NOTIFICATION_TYPES")}</label>
          </div>
          {values.actionTypes.map((value, index) => {
            const actionType = value.actionType;
            const atField = field.actionTypes.get(index);

            const configData = actionConfigurations?.find(
              ac => ac?.type.type === actionType.type
            );

            const frequencyOptions = getFrequencyOptions(configData!);
            return (
              <div className="form-group" key={value.actionType.id}>
                <label
                  className="col-3 text-right"
                  htmlFor={atField.enabled.name}
                >
                  {t(
                    NOTIFICATION_ACTION_TYPES[actionType.type] ??
                      actionType.type
                  )}
                  :
                </label>
                <div className="col-1">
                  <ToggleSwitch
                    {...atField.enabled}
                    disabled={isSubmitting}
                    id={atField.enabled.id}
                  />
                </div>
                {value.enabled && actionType.type === "DEVICE_OFFLINE" && (
                  <div
                    className="col-3 col-xl-2"
                    data-testid="interval-dropdown"
                  >
                    <Select
                      placeholder="Frequency"
                      {...atField.rate}
                      options={frequencyOptions}
                    />
                  </div>
                )}
              </div>
            );
          })}
          <div className="btn-container mb-1 ">
            <Link
              type="btn"
              className="btn"
              data-testid="cancel"
              to={"../service-now"}
              replace
            >
              {t("CANCEL")}
            </Link>
            <TestConnectionButton
              onClick={async () => {
                setShowTestSpinnerValue(true);
                const { password, url, username } = values;
                await test({
                  tenantId: id,
                  password,
                  url,
                  username
                });
                setShowTestSpinnerValue(false);
                return;
              }}
              isSubmitting={isSubmitting}
              isValid={isValid}
              showTestSpinner={showTestSpinner}
            />
            <SubmitButton
              isSubmitting={isSubmitting}
              disabled={!isValid || showTestSpinner}
              labelKey="NOTIFICATION_SERVICE_CREATE"
              submittingLabelKey="SAVING"
            />
          </div>
        </Form>
      )}
    </Formik>
  );
};
